import React, { useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { ModernPlanCardContainer } from "./ModernPlanCard.styles";
import { CheckIcon } from "./CheckIcon";
import { Loader } from "components/Loader";
import { contains } from "utils";
import { userDetailSelect } from "Layout/slice/selector";
import { planCardBg1, planCardBg2, planCardBg3 } from "assets/images";
// import { useNavigation } from "utils/navigate"; // Removed - no longer using Contact Sales
// import { CAL_LINK } from "utils/constants"; // Removed - no longer using Contact Sales

export const ModernPlanCard = ({
  plan,
  duration = "monthly",
  // getPremiumPlan = () => {},
  planCheckoutId = "",
  // handleContinuePremiumPlan = () => {},
  handlePlanSubscription = null,
  index = 0,
}) => {
  const [searchParams] = useSearchParams();
  const discountCodeParam = searchParams.get("discount_code");
  const userDetails = useSelector(userDetailSelect);

  // Check if this is the user's current active plan
  // Handle premium -> pro plan mapping (e.g., surmount_premium_yearly -> surmount_pro_yearly)
  const currentPlan = userDetails?.surmount_plan;
  let currentPlanId = currentPlan?.id;

  if (
    currentPlan &&
    contains(currentPlan.name?.toLowerCase() || "", "premium")
  ) {
    currentPlanId = currentPlan.id.replace("premium", "pro");
  }

  const isActivePlan =
    plan.id === currentPlanId &&
    userDetails?.stripe_subscription?.cancel_at === null;

  // Removed Contact Sales functionality

  const handleButtonClick = () => {
    if (isActivePlan) {
      return;
    }

    if (handlePlanSubscription) {
      handlePlanSubscription(plan.id);
    } else {
      console.error("handlePlanSubscription function is not available");
    }
  };

  // Use plan prices directly from API (no duration-based recalculation)
  const originalPrice = plan.price?.toString() || "0";
  const discountPrice = plan.discounted_price?.toString() || "0";

  const splitOrgPrice = originalPrice ? originalPrice.split(".") : ["0"];
  const splitDiscountPrice = discountPrice ? discountPrice.split(".") : ["0"];

  // Get plan color scheme based on plan name
  const getPlanColorScheme = () => {
    const planName = plan.name?.toLowerCase() || "";
    if (planName.includes("scale")) {
      return "purple";
    } else if (planName.includes("grow")) {
      return "blue";
    } else if (planName.includes("build")) {
      return "teal";
    }
    return "purple";
  };

  const colorScheme = getPlanColorScheme();

  // Get background image based on index, cycling through the 3 images
  const getBackgroundImage = () => {
    const backgrounds = [planCardBg1, planCardBg2, planCardBg3];
    return backgrounds[index % 3];
  };

  const getHeaderColorClass = () => {
    const id = plan.id?.toLowerCase() || "";
    const dur = duration?.toLowerCase() || "";

    if (id.includes("free")) return "header-silver";
    if (id.includes("core") && dur === "monthly") return "header-green";
    if (id.includes("core") && dur === "yearly") return "header-green";
    if (id.includes("plus") && dur === "monthly") return "header-blue";
    if (id.includes("plus") && dur === "yearly") return "header-blue";
    if (id.includes("pro") && dur === "monthly") return "header-warm";
    if (id.includes("pro") && dur === "yearly") return "header-warm";

    return "header-purple";
  };

  const headerColorClass = getHeaderColorClass();

  // map backend duration values ("yearly"/"monthly") to display labels ("year"/"month")
  const displayDuration = useMemo(() => {
    const d = (duration || "").toLowerCase();
    if (d === "yearly") return "yearly";
    if (d === "monthly") return "monthly";
    return d;
  }, [duration]);

  // const planDescriptionText = useMemo(() => {
  //   const isFree = plan?.interval == null;

  //   // If this is the user's active plan, show renewal/active messaging
  //   if (isActivePlan) {
  //     if (isFree) {
  //       return "You are on the free plan";
  //     }
  //     const billingTimestamp =
  //       userDetails?.stripe_subscription?.current_period_end;
  //     if (billingTimestamp) {
  //       const renewalDate = new Date(billingTimestamp * 1000).toLocaleString(
  //         "en-US",
  //         { month: "short", day: "numeric", year: "numeric" }
  //       );
  //       return `Renews on ${renewalDate}`;
  //     }
  //     return "Your subscription is active";
  //   }

  //   // For non-active plans, show the expected billing start date
  //   if (isFree) return "No credit card required";

  //   const billingPeriod =
  //     duration === "monthly"
  //       ? "monthly"
  //       : duration === "yearly"
  //       ? "yearly"
  //       : duration;

  //   const startDate = new Date().toLocaleString("en-US", {
  //     month: "short",
  //     day: "numeric",
  //     year: "numeric",
  //   });

  //   return `Billing ${billingPeriod} starting ${startDate}`;
  // }, [
  //   plan?.interval,
  //   duration,
  //   isActivePlan,
  //   userDetails?.stripe_subscription?.current_period_end,
  // ]);
  const formatYearlyBadge = (msg) => {
    if (!msg) return msg;
    if (msg.trim().toLowerCase() === "saved 2 months") {
      return "2 months free";
    }
    return msg;
  };
  return (
    <ModernPlanCardContainer
      className={`plan-card ${colorScheme}`}
      backgroundImage={getBackgroundImage()}
    >
      <div className={`plan-header ${headerColorClass}`}>
        <h3 className="plan-name">{plan.name}</h3>

        <div className="pricing-section">
          {Object.hasOwnProperty.call(plan, "price") && (
            <>
              <div className="price-display">
                <span
                  className={classNames("price-amount", {
                    "line-through": discountCodeParam,
                  })}
                >
                  ${splitOrgPrice[0]}
                  {splitOrgPrice[1] && splitOrgPrice[1] !== "00" && (
                    <span className="dot-price">.{splitOrgPrice[1]}</span>
                  )}
                </span>
                {discountCodeParam && (
                  <span className="price-amount discounted">
                    ${splitDiscountPrice[0]}
                    {splitDiscountPrice[1] && (
                      <span className="dot-price">
                        .{splitDiscountPrice[1]}
                      </span>
                    )}
                  </span>
                )}
                {plan?.interval ? (
                  <span className="price-period">{displayDuration}</span>
                ) : null}
                {plan.yearly_badge_message && (
                  <div className="yearly-badge">
                    {formatYearlyBadge(plan.yearly_badge_message)}
                  </div>
                )}
              </div>
            </>
          )}

          {contains(plan.id, "professional") && (
            <>
              <span className="price-amount custom">Custom</span>
              <div className="custom-description">
                Tools for financial advisors, trading groups and more!
              </div>
            </>
          )}
        </div>

        {/* <p className="plan-description">{planDescriptionText}</p> */}
      </div>

      <div className="plan-details-wrapper">
        <div className="button-section">
          <button
            className="plan-button"
            onClick={handleButtonClick}
            disabled={
              isActivePlan ||
              (contains(userDetails?.surmount_plan?.id, "free") &&
                contains(plan.id, "free"))
            }
          >
            {planCheckoutId === plan.id && <Loader className="button-loader" />}
            <span className="button-text">
              {isActivePlan ? "Your current plan" : plan.name}
            </span>
          </button>
        </div>

        <div className="benefits-section">
          <p>Includes:</p>

          <ul className="benefits-list">
            {plan.benefits?.map((benefit, index) => (
              <li key={index} className="benefit-item">
                <div className="check-icon">
                  <CheckIcon width={16} height={12} />
                </div>
                <div className="benefit-content">
                  {benefit.type === "special-text" && (
                    <span
                      className="benefit-tag"
                      style={{
                        color: "black",
                        backgroundColor: benefit.tag?.background_color,
                      }}
                    >
                      {benefit.tag?.label}
                    </span>
                  )}
                  <span className="benefit-text">{benefit.text}</span>
                </div>
              </li>
            )) || []}
          </ul>
        </div>
      </div>
    </ModernPlanCardContainer>
  );
};

ModernPlanCard.propTypes = {
  plan: PropTypes.object.isRequired,
  duration: PropTypes.string,
  getPremiumPlan: PropTypes.func,
  planCheckoutId: PropTypes.string,
  handleContinuePremiumPlan: PropTypes.func,
  handlePlanSubscription: PropTypes.func,
  index: PropTypes.number,
};
